import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BatchReturn, ReturnOrderLine, ReturnSlip } from 'returnCenter-api';
import styled from 'styled-components';
import { faCheck, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from 'components/ui/Button';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import { Dialog } from 'components/ui/Modal/Modal';
import { HandleReturnItemChange } from 'components/Orders/Order';
import { ExtendedReturnItem } from 'store/reducers/shared/sliceTypes';
import CreateBatchReturnModal from 'components/Orders/CreateBatchReturnModal';

type BatchReturnModalProps = {
    open: boolean;
    onClose: () => void;
    returnSlip: ReturnSlip | null;
    disabled?: boolean;
    handleReturnItemChange: HandleReturnItemChange;
    batchDestinationNames: Record<number, string>;
    openBatchReturns: BatchReturn[];
    onApprove: () => void;
    onUnapprove: () => void;
};

type BatchEligibleItem = {
    orderNumber: string;
    orderline: ReturnOrderLine;
    returnItem: ExtendedReturnItem;
};

const GREEN_COLOR = '#319345';

const BatchReturnModal = ({
    open,
    onClose,
    returnSlip,
    disabled = false,
    handleReturnItemChange,
    batchDestinationNames,
    openBatchReturns,
    onApprove,
    onUnapprove,
}: BatchReturnModalProps) => {
    const { t } = useTranslation();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createDestinationId, setCreateDestinationId] = useState<number | undefined>(undefined);

    // Collect all batch-eligible items grouped by destination
    const groupedItems = useMemo(() => {
        const groups = new Map<number, BatchEligibleItem[]>();
        if (!returnSlip) return groups;

        for (const orderGroup of returnSlip.returnItems ?? []) {
            for (const line of orderGroup.orderLines ?? []) {
                for (const item of (line.returnItems ?? []) as ExtendedReturnItem[]) {
                    if (item.batchReturnDestinationId != null) {
                        const destId = item.batchReturnDestinationId;
                        if (!groups.has(destId)) groups.set(destId, []);
                        groups.get(destId)!.push({
                            orderNumber: orderGroup.orderNumber ?? '',
                            orderline: line,
                            returnItem: item,
                        });
                    }
                }
            }
        }
        return groups;
    }, [returnSlip]);

    // Local assignment state: itemKey -> batchReturnId
    const [assignments, setAssignments] = useState<Map<string, number | null>>(new Map());

    const getItemKey = (item: BatchEligibleItem) =>
        `${item.orderNumber}-${item.orderline.sku}-${item.orderline.orderLineNumber}-${
            item.returnItem.id ?? item.returnItem.splitId
        }`;

    const getAssignment = (item: BatchEligibleItem) => {
        const key = getItemKey(item);
        if (assignments.has(key)) return assignments.get(key)!;
        return item.returnItem.batchReturnId ?? null;
    };

    const setAssignment = (item: BatchEligibleItem, value: number | null) => {
        setAssignments((prev) => {
            const next = new Map(prev);
            next.set(getItemKey(item), value);
            return next;
        });
    };

    // Assign all unassigned items in a destination group to the first available open batch return
    const handleAutoAssign = (destId: number) => {
        const items = groupedItems.get(destId);
        if (!items) return;

        const availableBatch = openBatchReturns.find((br) => br.batchDestinationId === destId);
        if (!availableBatch?.id) return;

        setAssignments((prev) => {
            const next = new Map(prev);
            for (const item of items) {
                if (getAssignment(item) == null) {
                    next.set(getItemKey(item), availableBatch.id ?? null);
                }
            }
            return next;
        });
    };

    // Compute whether all batch-eligible items are assigned
    const allBatchItemsAssigned = useMemo(() => {
        if (groupedItems.size === 0) return false;
        for (const [, items] of groupedItems) {
            for (const item of items) {
                if (getAssignment(item) == null) return false;
            }
        }
        return true;
    }, [groupedItems, assignments]);

    // Per-destination assignment status
    const getGroupAssignmentStatus = (items: BatchEligibleItem[]) => {
        const total = items.length;
        const assigned = items.filter((item) => getAssignment(item) != null).length;
        return { total, assigned, allAssigned: assigned === total };
    };

    const handleSave = () => {
        for (const [, items] of groupedItems) {
            for (const item of items) {
                const newValue = getAssignment(item);
                const oldValue = item.returnItem.batchReturnId ?? null;
                if (newValue !== oldValue) {
                    const sku = item.orderline.sku ?? '';
                    const orderLineNumber = item.orderline.orderLineNumber ?? '';
                    const itemId = item.returnItem.id ?? item.returnItem.splitId ?? -1;
                    handleReturnItemChange(
                        item.orderNumber,
                        sku,
                        orderLineNumber,
                        itemId,
                        'batchReturnId',
                        newValue ?? null,
                    );
                }
            }
        }
        setAssignments(new Map());
        onClose();
    };

    const handleApprove = () => {
        // Save all pending assignments first
        for (const [, items] of groupedItems) {
            for (const item of items) {
                const newValue = getAssignment(item);
                const oldValue = item.returnItem.batchReturnId ?? null;
                if (newValue !== oldValue) {
                    const sku = item.orderline.sku ?? '';
                    const orderLineNumber = item.orderline.orderLineNumber ?? '';
                    const itemId = item.returnItem.id ?? item.returnItem.splitId ?? -1;
                    handleReturnItemChange(
                        item.orderNumber,
                        sku,
                        orderLineNumber,
                        itemId,
                        'batchReturnId',
                        newValue ?? null,
                    );
                }
            }
        }
        setAssignments(new Map());
        onApprove();
        onClose();
    };

    const handleCreateNew = (destinationId: number) => {
        setCreateDestinationId(destinationId);
        setShowCreateModal(true);
    };

    if (!open) return null;

    return (
        <Dialog
            onClose={() => {
                setAssignments(new Map());
                onClose();
            }}
            header={t('batchReturn.manageBatchReturns', 'Manage Batch Returns')}
            tint
            portal
            width="55%"
            maxHeight="80vh"
        >
            <ModalContent className="modal-content mt-5 p-3" $grow={1} column gap="1.5rem">
                {Array.from(groupedItems.entries()).map(([destId, items]) => {
                    const destName = batchDestinationNames[destId] ?? `Destination #${destId}`;
                    const availableBatches = openBatchReturns.filter((br) => br.batchDestinationId === destId);
                    const { assigned, total, allAssigned } = getGroupAssignmentStatus(items);

                    return (
                        <DestinationGroup
                            key={destId}
                            $allAssigned={allAssigned}
                            className="destination-group p-3"
                            column
                            gap="0.75rem"
                        >
                            <Flex justify="between" align="center">
                                <Flex align="center" gap="0.5rem">
                                    <Text fontSize={14} bold>
                                        {destName}
                                    </Text>
                                    <StatusIndicator $allAssigned={allAssigned}>
                                        <FontAwesomeIcon
                                            icon={allAssigned ? faCheck : faExclamationTriangle}
                                            size="sm"
                                        />
                                        <span>
                                            {assigned}/{total}
                                        </span>
                                    </StatusIndicator>
                                </Flex>
                                <Flex gap="0.5rem">
                                    <Button
                                        rounded
                                        onClick={() => handleAutoAssign(destId)}
                                        type="button"
                                        disabled={disabled || allAssigned}
                                    >
                                        {t('batchReturn.assignAll', 'Assign all')}
                                    </Button>
                                    <Button
                                        rounded
                                        onClick={() => handleCreateNew(destId)}
                                        type="button"
                                        disabled={disabled}
                                    >
                                        {t('batchReturn.createNew', 'Create new')}
                                    </Button>
                                </Flex>
                            </Flex>

                            <ItemsTable>
                                <thead>
                                    <tr>
                                        <th>{t('sku', 'SKU')}</th>
                                        <th>{t('productInformation', 'Product')}</th>
                                        <th>{t('returnQty', 'Qty')}</th>
                                        <th>{t('batchReturn.batchReturn', 'Batch Return')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={getItemKey(item)}>
                                            <td>{item.orderline.sku}</td>
                                            <td>{item.orderline.productName}</td>
                                            <td>{item.returnItem.quantityInReturn ?? 0}</td>
                                            <td>
                                                <Select
                                                    value={getAssignment(item) ?? ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setAssignment(item, val ? Number(val) : null);
                                                    }}
                                                    disabled={disabled}
                                                >
                                                    <option value="">
                                                        {t('batchReturn.notAssigned', '-- Not assigned --')}
                                                    </option>
                                                    {availableBatches.map((br) => (
                                                        <option key={br.id} value={br.id}>
                                                            #{br.id} {br.batchDestinationName}{' '}
                                                            {br.comment ? `(${br.comment})` : ''} (
                                                            {new Date(br.created!).toLocaleDateString()})
                                                        </option>
                                                    ))}
                                                </Select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </ItemsTable>
                        </DestinationGroup>
                    );
                })}

                {groupedItems.size === 0 && (
                    <Text fontSize={14} color="textFaded">
                        {t('batchReturn.noEligibleItems', 'No batch-eligible items found.')}
                    </Text>
                )}
            </ModalContent>

            <Flex justify="between" align="center" className="bottom-section p-3">
                <StyledText fontSize={12} $color={allBatchItemsAssigned}>
                    {allBatchItemsAssigned
                        ? t('batchReturn.allItemsAssigned', 'All items are assigned to a batch return')
                        : t('batchReturn.notAllItemsAssigned', 'Not all items are assigned')}
                </StyledText>
                <Flex gap="0.5rem">
                    <Button
                        rounded
                        onClick={() => {
                            setAssignments(new Map());
                            onClose();
                        }}
                        type="button"
                    >
                        {t('cancel')}
                    </Button>
                    <Button rounded onClick={handleSave} type="button" disabled={disabled}>
                        {t('save')}
                    </Button>
                    {returnSlip?.batchHandled ? (
                        <Button
                            rounded
                            onClick={() => {
                                onUnapprove();
                                onClose();
                            }}
                            type="button"
                            disabled={disabled}
                        >
                            {t('batchReturn.unapprove', 'Unapprove')}
                        </Button>
                    ) : (
                        <Button rounded main onClick={handleApprove} type="button" disabled={disabled}>
                            {t('batchReturn.approve', 'Approve')}
                        </Button>
                    )}
                </Flex>
            </Flex>

            <CreateBatchReturnModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={() => setShowCreateModal(false)}
                preselectedDestinationId={createDestinationId}
            />
        </Dialog>
    );
};

export default BatchReturnModal;

const ModalContent = styled(Flex)`
    max-height: 60vh;
    overflow-y: auto;
    padding: 1rem;
`;

const DestinationGroup = styled(Flex)<{ $allAssigned?: boolean }>`
    border: 1px solid ${({ theme, $allAssigned }) => ($allAssigned ? GREEN_COLOR : theme.colors.border)};
    border-radius: var(--radius);
`;

const StatusIndicator = styled.span<{ $allAssigned: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 12px;
    color: ${({ $allAssigned }) => ($allAssigned ? GREEN_COLOR : '#f59e0b')};
`;

const ItemsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th,
    td {
        padding: 0.5rem;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    th {
        font-weight: 600;
        font-size: 12px;
        text-transform: uppercase;
    }
`;

const Select = styled.select`
    padding: 0.35rem 0.5rem;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    font-size: 13px;
    width: 100%;
`;

const StyledText = styled(Text)<{ $color?: boolean }>`
    color: ${({ $color, theme }) => ($color ? GREEN_COLOR : theme.colors.textFaded)};
`;
